<template>
  <form>
    <div class="d-flex flex-row nowrap justify-content-between heading">
      <span></span>
    </div>

    <InformationsForm
      :form="registerForm"
      :isEmailValid="isEmailValid"
      :show-errors="state.showErrors"
      :invalid-fields="invalidFields"
      @updateForm="newForm => handleUpdateForm(newForm)"
      @useOtherEmail="handleUseOtherEmail"
    />
    <ContactsForm
      :form="registerForm"
      :show-errors="state.showErrors"
      :invalid-fields="invalidFields"
      @updateForm="newForm => handleUpdateForm(newForm)"
    />
    <KeepInTouchOptIn v-if="showCdaOptIn" />
    <div class="my-4">
      <div>
        <Button
          class="px-2"
          :disabled="btnDisabled"
          @buttonClick="handleButtonClick"
        >
          <template #text>
            <span>{{ t("loginRegister.validate") }}</span>
          </template>
        </Button>
      </div>
    </div>
    <Banner v-if="state.isDisplayGeneralErrorMessage" :background="'#D4572A1A'">
      <template #text>
        <span :style="{ color: '#D4572A' }">
          {{ state.isDisplayGeneralErrorMessage }}
        </span>
      </template>
    </Banner>
    <ModalBase id="register-call-center-modal" ref="callCenterModalRef">
      <div class="register-call-center-modal">
        <div class="register-call-center-modal-header">
          <h3>{{ t('loginRegister.register_call_center_modal_title') }}</h3>
        </div>
        <div class="register-call-center-modal-body">
          <p>{{ t('loginRegister.register_call_center_modal_message') }}</p>
          <div
            v-if="callCenterContactInfo.openingHours"
            class="register-call-center-info"
          >
            <span class="register-call-center-info-label">
              {{ t('loginRegister.register_call_center_modal_opening_hours') }}
            </span>
            <span>{{ callCenterContactInfo.openingHours }}</span>
          </div>
          <div
            v-if="callCenterContactInfo.phone"
            class="register-call-center-info"
          >
            <span class="register-call-center-info-label">
              {{ t('loginRegister.register_call_center_modal_phone') }}
            </span>
            <span class="register-call-center-phone-desktop">
              {{ callCenterContactInfo.phone }}
            </span>
            <a class="register-call-center-phone-mobile" :href="callCenterPhoneHref">
              {{ callCenterContactInfo.phone }}
            </a>
          </div>
          <a
            v-if="callCenterContactInfo.phone"
            class="register-call-center-call-button"
            :href="callCenterPhoneHref"
          >
            {{ t('loginRegister.register_call_center_modal_call_button') }}
          </a>
        </div>
      </div>
      <template #footer>
        <span />
      </template>
    </ModalBase>
  </form>
</template>

<script lang="ts" setup>
  import {
    reactive,
    computed,
    defineEmits,
    onMounted,
    onBeforeUnmount,
    watch,
    ref,
  } from 'vue'
  import InformationsForm from '@/components/LoginRegister/subforms/InformationsForm/v1/InformationsForm.vue'
  import ContactsForm from '@/components/LoginRegister/subforms/ContactsForm/v1/ContactsForm.vue'
  import KeepInTouchOptIn from '@/components/LoginRegister/KeepInTouchOptIn/KeepInTouchOptIn.vue'
  import Button from '@/components/LoginRegister/common/Button/v1/Button.vue'
  import { isCdaOptInEnabledForAll } from '@/logic/contact/isCdaOptInEnabled'
  import Banner from '@/components/LoginRegister/common/Banner/v1/Banner.vue'
  import ModalBase from '@/components/UI/ModalBase.vue'
  import { onKeyStroke } from '@vueuse/core'
  import useIsEmailValid from '@/composables/useIsEmailValid'
  import scrollIntoView from 'smooth-scroll-into-view-if-needed'

  import RegisterForm from '@/model/Form/RegisterForm'
  import registerForm from '@/state/registerForm'
  import useLoading from '@/composables/useLoading'
  import { useValidation } from '@/composables/useValidation'

  import register from '@/logic/contact/register'
  import resetRegisterForm from '@/logic/user-info/resetRegisterForm'
  import email from '@/state/email'
  import { isPhoneNumberValid, getInvalidPhoneMessage } from '@/components/LoginRegister/phoneNumberValidation'
  import {
    getHeaderContactInfo,
    HeaderContactInfo,
  } from '@/logic/headerFooter/contactInfo'

  import * as _ from 'lodash'

  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  const minPasswordLength = 6

  interface Emits {
    (e: 'registrationSuccess'): void
    (e: 'emailChange', email: string): void
    (e: 'useOtherEmail'): void
  }

  const state = reactive({
    showErrors: false,
    isDisplayGeneralErrorMessage: null as string | null,
  })

  registerForm.value = new RegisterForm(email.value)

  const emit = defineEmits<Emits>()

  /** Register has no iresa id yet — only show when allowlist is `*` / `all`. */
  const showCdaOptIn = computed(() => isCdaOptInEnabledForAll())
  const btnDisabled = computed(() => {
    return state.showErrors && invalidFields.value.size > 0 ? true : false
  })
  const isEmailValid = useIsEmailValid(email)

  const validationRules = {
    first_name: (value: string) => value.trim().length > 0,
    last_name: (value: string) => value.trim().length > 0,
    email: () => useIsEmailValid(email).value,
    password1: (value: string) => value.length > minPasswordLength,
    password2: (value: string) => value.length > minPasswordLength,
    address: (value: string) => value.trim().length > 0,
    city: (value: string) => value.trim().length > 0,
    postal_code: (value: string) => value.trim().length > 0,
    country: (value: string) => value.trim().length > 0,
    phone_code: (value: string) => value.trim().length > 0,
    phone_number: (value: string) =>
      isPhoneNumberValid(value, registerForm.value?.phone_code || ''),
  }

  const { invalidFields } = useValidation<RegisterForm | null>(
    registerForm,
    validationRules
  )

  const submitClickCount = ref<number>(0)
  const isCallCenterMode = ref<boolean>(false)
  const pendingRetryAfterPhoneChange = ref<boolean>(false)
  const callCenterModalRef = ref<InstanceType<typeof ModalBase> | null>(null)
  const callCenterContactInfo = ref<HeaderContactInfo>({
    phone: '',
    openingHours: '',
    customerServiceLabel: '',
  })
  const callCenterPhoneHref = computed(
    () => `tel:${callCenterContactInfo.value.phone.replace(/\s/g, '')}`
  )

  const showCallCenterModal = async () => {
    callCenterContactInfo.value = await getHeaderContactInfo()
    isCallCenterMode.value = true
    callCenterModalRef.value?.open()
  }

  const handleButtonClick = async () => {
    if (!registerForm.value)
      throw new Error('registerForm.value cannot be null')

    state.isDisplayGeneralErrorMessage = null

    if (isCallCenterMode.value) {
      await showCallCenterModal()
      return
    }

    const isPhoneChangeRetry = pendingRetryAfterPhoneChange.value
    if (!isPhoneChangeRetry) {
      submitClickCount.value += 1
      if (submitClickCount.value > 3) {
        await showCallCenterModal()
        return
      }
    }

    if (invalidFields.value.size > 0) {
      state.showErrors = true
      const selector = `#${[...invalidFields.value][0]}`
      const invalidFieldElement = document.querySelector(
        selector
      ) as HTMLElement

      await scrollIntoView(invalidFieldElement, {
        ease: t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
        duration: 750,
        block: 'center',
        inline: 'nearest',
      })
      return
    }
    state.showErrors = false

    if (isPhoneChangeRetry) {
      pendingRetryAfterPhoneChange.value = false
    }

    const { startLoadingThis, stopLoadingThis } = useLoading()
    startLoadingThis()
    try {
      await register(registerForm.value)
      state.isDisplayGeneralErrorMessage = null
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ''
      if (message.includes('phone_number')) {
        state.isDisplayGeneralErrorMessage = getInvalidPhoneMessage(
          t,
          registerForm.value.phone_code
        )
      } else {
        state.isDisplayGeneralErrorMessage = t(
          'loginRegister.error_wrong_email'
        )
      }
      if (isPhoneChangeRetry) {
        isCallCenterMode.value = true
      }
    }
    stopLoadingThis()
  }

  const handleUpdateForm = (newForm: RegisterForm) => {
    const didPhoneChange =
      newForm.phone_number !== registerForm.value?.phone_number ||
      newForm.phone_code !== registerForm.value?.phone_code
    const formClone = _.cloneDeep(newForm)
    registerForm.value = formClone
    if (didPhoneChange && isCallCenterMode.value) {
      isCallCenterMode.value = false
      pendingRetryAfterPhoneChange.value = true
      state.isDisplayGeneralErrorMessage = null
    }
  }
  const handleUseOtherEmail = () => {
    emit('useOtherEmail')
  }

  onMounted(async () => {
    await resetRegisterForm()

    const stop = onKeyStroke('Enter', () => {
      handleButtonClick()
    })
    onBeforeUnmount(() => stop())
  })

  watch(() => email.value, resetRegisterForm)
</script>

<style scoped>
.register-call-center-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.register-call-center-modal-header h3 {
  margin: 0;
  color: var(--grey-900);
  font-size: 20px;
  font-weight: 600;
}

.register-call-center-modal-body {
  color: var(--grey-900);
  font-size: 16px;
  line-height: 1.5;
}

.register-call-center-modal-body p {
  margin: 0 0 16px;
}

.register-call-center-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
}

.register-call-center-info-label {
  font-weight: 700;
}

.register-call-center-phone-mobile {
  display: none;
}

.register-call-center-call-button {
  display: none;
  width: 100%;
  margin-top: 24px;
  padding: 14px 20px;
  color: var(--primary-white, #fff);
  text-align: center;
  text-decoration: none;
  font-weight: 700;
  background: var(--primary-blue, #0b3d91);
  border-radius: 24px;
}

@media (max-width: 767px) {
  .register-call-center-phone-desktop {
    display: none;
  }

  .register-call-center-phone-mobile {
    display: inline;
  }

  .register-call-center-call-button {
    display: block;
  }
}
</style>
